import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Badge } from '../components/ui/badge';
import { 
  Building2, 
  Users, 
  Crown, 
  Search, 
  RefreshCw,
  Edit2,
  Ban,
  CheckCircle,
  Mail,
  Calendar,
  Sparkles
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const TenantManagement = () => {
  const { user } = useAuth();
  const [tenants, setTenants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterTier, setFilterTier] = useState('all');
  const [editingTenant, setEditingTenant] = useState(null);
  const [editForm, setEditForm] = useState({ name: '', tier: '' });

  const fetchTenants = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/api/tenants`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setTenants(response.data);
    } catch (error) {
      toast.error('Failed to fetch tenants');
      console.error('Error fetching tenants:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchTenants();
  }, [fetchTenants]);

  const handleEditTenant = (tenant) => {
    setEditingTenant(tenant);
    setEditForm({ name: tenant.name, tier: tenant.tier });
  };

  const handleSaveEdit = async () => {
    if (!editingTenant) return;

    try {
      const token = localStorage.getItem('token');
      await axios.put(
        `${API_URL}/api/tenants/${editingTenant.id}`,
        { name: editForm.name, tier: editForm.tier },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Tenant updated successfully');
      setEditingTenant(null);
      fetchTenants();
    } catch (error) {
      toast.error('Failed to update tenant');
    }
  };

  const handleToggleActive = async (tenant) => {
    try {
      const token = localStorage.getItem('token');
      await axios.put(
        `${API_URL}/api/tenants/${tenant.id}/toggle-active`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(tenant.is_active ? 'Tenant deactivated' : 'Tenant activated');
      fetchTenants();
    } catch (error) {
      toast.error('Failed to update tenant status');
    }
  };

  const getTierBadge = (tier) => {
    const colors = {
      'Basic': 'bg-gray-100 text-gray-800',
      'Pro': 'bg-blue-100 text-blue-800',
      'Enterprise': 'bg-purple-100 text-purple-800'
    };
    return (
      <Badge className={`${colors[tier] || colors.Basic} flex items-center gap-1`}>
        {tier === 'Enterprise' && <Crown className="h-3 w-3" />}
        {tier}
      </Badge>
    );
  };

  const filteredTenants = tenants.filter(tenant => {
    const matchesSearch = 
      tenant.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      tenant.owner_email.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesTier = filterTier === 'all' || tenant.tier === filterTier;
    return matchesSearch && matchesTier;
  });

  const stats = {
    total: tenants.length,
    basic: tenants.filter(t => t.tier === 'Basic').length,
    pro: tenants.filter(t => t.tier === 'Pro').length,
    enterprise: tenants.filter(t => t.tier === 'Enterprise').length,
    active: tenants.filter(t => t.is_active).length,
    totalMembers: tenants.reduce((sum, t) => sum + (t.member_count || 0), 0)
  };

  if (user?.role !== 'Admin') {
    return (
      <div className="p-8 text-center">
        <h1 className="text-2xl font-bold text-red-600">Access Denied</h1>
        <p className="text-gray-600 mt-2">Only Super Admins can access this page.</p>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6" data-testid="tenant-management-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2" data-testid="tenant-management-title">
            <Building2 className="h-7 w-7 text-[#E31837]" />
            Tenant Management
          </h1>
          <p className="text-gray-500 mt-1">Manage all teams and their subscriptions</p>
        </div>
        <Button
          onClick={fetchTenants}
          variant="outline"
          className="flex items-center gap-2"
          disabled={loading}
          data-testid="tenant-refresh-button"
        >
          <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
          Refresh
        </Button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
        <Card className="bg-gradient-to-br from-gray-50 to-white">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-gray-100 rounded-lg">
                <Building2 className="h-5 w-5 text-gray-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{stats.total}</p>
                <p className="text-xs text-gray-500">Total Teams</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card className="bg-gradient-to-br from-green-50 to-white">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-green-100 rounded-lg">
                <CheckCircle className="h-5 w-5 text-green-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{stats.active}</p>
                <p className="text-xs text-gray-500">Active</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-gray-50 to-white">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-gray-200 rounded-lg">
                <Sparkles className="h-5 w-5 text-gray-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{stats.basic}</p>
                <p className="text-xs text-gray-500">Basic</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-blue-50 to-white">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-100 rounded-lg">
                <Sparkles className="h-5 w-5 text-blue-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{stats.pro}</p>
                <p className="text-xs text-gray-500">Pro</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-purple-50 to-white">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-purple-100 rounded-lg">
                <Crown className="h-5 w-5 text-purple-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{stats.enterprise}</p>
                <p className="text-xs text-gray-500">Enterprise</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-[#E31837]/10 to-white">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-[#E31837]/20 rounded-lg">
                <Users className="h-5 w-5 text-[#E31837]" />
              </div>
              <div>
                <p className="text-2xl font-bold">{stats.totalMembers}</p>
                <p className="text-xs text-gray-500">Total Users</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="flex-1">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                <Input
                  placeholder="Search by team name or owner email..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10"
                  data-testid="tenant-search-input"
                />
              </div>
            </div>
            <div className="w-full md:w-48">
              <Select value={filterTier} onValueChange={setFilterTier}>
                <SelectTrigger data-testid="tenant-tier-filter">
                  <SelectValue placeholder="Filter by tier" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Tiers</SelectItem>
                  <SelectItem value="Basic">Basic</SelectItem>
                  <SelectItem value="Pro">Pro</SelectItem>
                  <SelectItem value="Enterprise">Enterprise</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Tenants Table */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">All Teams ({filteredTenants.length})</CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <RefreshCw className="h-8 w-8 animate-spin text-gray-400" />
            </div>
          ) : filteredTenants.length === 0 ? (
            <div className="text-center py-12 text-gray-500">
              {searchTerm || filterTier !== 'all' 
                ? 'No teams match your filters' 
                : 'No teams found'}
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b bg-gray-50">
                    <th className="text-left p-3 font-medium text-gray-600">Team</th>
                    <th className="text-left p-3 font-medium text-gray-600">Owner</th>
                    <th className="text-left p-3 font-medium text-gray-600">Tier</th>
                    <th className="text-center p-3 font-medium text-gray-600">Members</th>
                    <th className="text-left p-3 font-medium text-gray-600">Status</th>
                    <th className="text-left p-3 font-medium text-gray-600">Created</th>
                    <th className="text-right p-3 font-medium text-gray-600">Actions</th>
                  </tr>
                </thead>
                <tbody data-testid="tenant-table-body">
                  {filteredTenants.map((tenant) => (
                    <tr key={tenant.id} className="border-b hover:bg-gray-50" data-testid={`tenant-row-${tenant.id}`}>
                      <td className="p-3">
                        <div className="flex items-center gap-2">
                          <Building2 className="h-4 w-4 text-gray-400" />
                          <span className="font-medium">{tenant.name}</span>
                          {tenant.is_owner_account && (
                            <Badge variant="outline" className="text-xs bg-yellow-50 text-yellow-700 border-yellow-200">
                              Owner
                            </Badge>
                          )}
                        </div>
                      </td>
                      <td className="p-3">
                        <div className="flex items-center gap-2 text-sm text-gray-600">
                          <Mail className="h-3 w-3" />
                          {tenant.owner_email}
                        </div>
                      </td>
                      <td className="p-3">{getTierBadge(tenant.tier)}</td>
                      <td className="p-3 text-center">
                        <span className="inline-flex items-center gap-1 text-sm">
                          <Users className="h-3 w-3" />
                          {tenant.member_count || 0}
                        </span>
                      </td>
                      <td className="p-3">
                        {tenant.is_active ? (
                          <Badge className="bg-green-100 text-green-800">Active</Badge>
                        ) : (
                          <Badge className="bg-red-100 text-red-800">Inactive</Badge>
                        )}
                      </td>
                      <td className="p-3">
                        <div className="flex items-center gap-1 text-sm text-gray-500">
                          <Calendar className="h-3 w-3" />
                          {new Date(tenant.created_at).toLocaleDateString()}
                        </div>
                      </td>
                      <td className="p-3 text-right">
                        <div className="flex items-center justify-end gap-2">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleEditTenant(tenant)}
                            className="h-8 w-8 p-0"
                            data-testid={`tenant-edit-button-${tenant.id}`}
                          >
                            <Edit2 className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleToggleActive(tenant)}
                            className={`h-8 w-8 p-0 ${tenant.is_active ? 'text-red-600 hover:text-red-700' : 'text-green-600 hover:text-green-700'}`}
                            disabled={tenant.is_owner_account}
                            title={tenant.is_owner_account ? 'Cannot deactivate owner account' : ''}
                            data-testid={`tenant-toggle-active-button-${tenant.id}`}
                          >
                            {tenant.is_active ? <Ban className="h-4 w-4" /> : <CheckCircle className="h-4 w-4" />}
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Edit Modal */}
      {editingTenant && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" data-testid="tenant-edit-modal">
          <Card className="w-full max-w-md mx-4">
            <CardHeader>
              <CardTitle>Edit Team: {editingTenant.name}</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label>Team Name</Label>
                <Input
                  value={editForm.name}
                  onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
                  className="mt-1"
                  data-testid="tenant-edit-name-input"
                />
              </div>
              <div>
                <Label>Subscription Tier</Label>
                <Select value={editForm.tier} onValueChange={(v) => setEditForm({ ...editForm, tier: v })}>
                  <SelectTrigger className="mt-1" data-testid="tenant-edit-tier-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Basic">Basic</SelectItem>
                    <SelectItem value="Pro">Pro</SelectItem>
                    <SelectItem value="Enterprise">Enterprise</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="flex gap-3 pt-4">
                <Button
                  variant="outline"
                  className="flex-1"
                  onClick={() => setEditingTenant(null)}
                  data-testid="tenant-edit-cancel-button"
                >
                  Cancel
                </Button>
                <Button
                  className="flex-1 bg-[#E31837] hover:bg-[#C4122C]"
                  onClick={handleSaveEdit}
                  data-testid="tenant-edit-save-button"
                >
                  Save Changes
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
};

export default TenantManagement;
